import { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useProperties } from '../contexts/PropertyContext';

export const SearchFilters = ({ onSearch, compact = false }) => {
  const { filters, updateFilters, clearFilters, amenities } = useProperties();
  const [localFilters, setLocalFilters] = useState(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    updateFilters(localFilters);
    onSearch?.(localFilters);
  };

  const handleClear = () => {
    const cleared = {
      transaction_type: '',
      property_type: '',
      neighborhood: '',
      price_min: '',
      price_max: '',
      bedrooms_min: '',
      bathrooms_min: '',
      sort_by: 'newest',
    };
    setLocalFilters(cleared);
    clearFilters();
    onSearch?.(cleared);
  };

  const propertyTypes = [
    { value: 'house', label: 'Casa' },
    { value: 'apartment', label: 'Apartamento' },
    { value: 'land', label: 'Terreno' },
    { value: 'commercial', label: 'Comercial' },
    { value: 'rural', label: 'Rural' },
  ];

  const transactionTypes = [
    { value: 'sale', label: 'Venda' },
    { value: 'rent', label: 'Aluguel' },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Mais recentes' },
    { value: 'price_asc', label: 'Menor preço' },
    { value: 'price_desc', label: 'Maior preço' },
    { value: 'area_desc', label: 'Maior área' },
  ];

  return (
    <div data-testid="search-filters" className={`bg-white rounded-2xl shadow-xl ${compact ? 'p-4' : 'p-6 md:p-8'}`}>
      {/* Main search row */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Transaction Type */}
        <div className="w-full md:w-40">
          <Select value={localFilters.transaction_type} onValueChange={(v) => handleChange('transaction_type', v)}>
            <SelectTrigger data-testid="transaction-type-select" className="h-12">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {transactionTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Property Type */}
        <div className="w-full md:w-48">
          <Select value={localFilters.property_type} onValueChange={(v) => handleChange('property_type', v)}>
            <SelectTrigger data-testid="property-type-select" className="h-12">
              <SelectValue placeholder="Imóvel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {propertyTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Neighborhood */}
        <div className="flex-1">
          <Input
            data-testid="neighborhood-input"
            placeholder="Bairro ou região..."
            value={localFilters.neighborhood}
            onChange={(e) => handleChange('neighborhood', e.target.value)}
            className="h-12"
          />
        </div>

        {/* Search Button */}
        <Button
          data-testid="search-button"
          onClick={handleSearch}
          className="h-12 px-8 bg-brand-blue hover:bg-brand-blue-dark text-white rounded-full"
        >
          <Search className="w-5 h-5 mr-2" />
          Buscar
        </Button>

        {/* Advanced Filters Toggle */}
        <Sheet open={showAdvanced} onOpenChange={setShowAdvanced}>
          <SheetTrigger asChild>
            <Button variant="outline" className="h-12 px-4" data-testid="advanced-filters-btn">
              <SlidersHorizontal className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-heading text-xl">Filtros Avançados</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              {/* Price Range */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Faixa de Preço</Label>
                <div className="flex gap-3">
                  <Input
                    type="number"
                    placeholder="Mínimo"
                    value={localFilters.price_min}
                    onChange={(e) => handleChange('price_min', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Máximo"
                    value={localFilters.price_max}
                    onChange={(e) => handleChange('price_max', e.target.value)}
                  />
                </div>
              </div>

              {/* Bedrooms */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Quartos (mínimo)</Label>
                <Select value={localFilters.bedrooms_min} onValueChange={(v) => handleChange('bedrooms_min', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bathrooms */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Banheiros (mínimo)</Label>
                <Select value={localFilters.bathrooms_min} onValueChange={(v) => handleChange('bathrooms_min', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}+</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sort */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Ordenar por</Label>
                <Select value={localFilters.sort_by} onValueChange={(v) => handleChange('sort_by', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={handleClear} className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Limpar
                </Button>
                <Button onClick={() => { handleSearch(); setShowAdvanced(false); }} className="flex-1 bg-brand-blue hover:bg-brand-blue-dark">
                  Aplicar Filtros
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};
